import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store';
import { criteriasApi } from '../../services/api/criterias.api';
import { settingsApi } from '../../services/api/settings.api';
import { gameApi } from '../../services/api/game.api';
import { sessionsApi } from '../../services/api/sessions.api';
import { CriteriaCard } from '../../components/game/CriteriaCard';
import { VerbatimModal } from '../../components/game/VerbatimModal';
import { FirstRoundModal } from '../../components/game/FirstRoundModal';
import { Loading } from '../../components/common/Loading';
import { getTeamColorClasses } from '../../utils/teamColors';
import { clearPlayerSessionStorage } from '../../utils/playerSessionStorage';
import { getPlayerRealtimeSocket } from '../../services/realtime/player.socket';
import { applyPlayerLanguage } from '../../utils/sessionI18n';
import {
  playerRealtimeEvents,
  type GameMode,
  type PlayerTeamResetEvent,
  type Criteria,
  TeamName,
} from '@business-game/shared';

export const Game: React.FC = () => {
  const navigate = useNavigate();
  const { sessionCode: sessionCodeFromPath } = useParams<{ sessionCode?: string }>();
  const { t, i18n } = useTranslation();
  const {
    teamName,
    teamId,
    currentRound,
    maxRounds,
    pointsPerRound,
    sessionCode,
    selectedCriterias,
    setSession,
    setGameConfig,
    selectCriteria,
    clearSelection,
    nextRound,
    resetGame,
    getTotalPoints,
    getRemainingPoints,
  } = useStore();

  // Component state
  const [criterias, setCriterias] = useState<Criteria[]>([]);
  const [sessionGameMode, setSessionGameMode] = useState<GameMode | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showFirstRoundModal, setShowFirstRoundModal] = useState(false);
  const [showVerbatimModal, setShowVerbatimModal] = useState(false);
  const [verbatimHtml, setVerbatimHtml] = useState('');
  const [lastVerbatim, setLastVerbatim] = useState<string | null>(null);

  const gameMode = sessionGameMode;
  const isLeadlab = gameMode === 'leadlab';

  const totalPoints = getTotalPoints();
  const remainingPoints = getRemainingPoints();
  const progressPercent = (currentRound / maxRounds) * 100;
  const effectiveSessionCode = sessionCodeFromPath || sessionCode || localStorage.getItem('session_code');

  // Get team color classes
  const teamColors = useMemo(
    () => getTeamColorClasses(teamName, gameMode),
    [teamName, gameMode]
  );

  useEffect(() => {
    if (sessionCodeFromPath && sessionCodeFromPath !== sessionCode) {
      setSession(sessionCodeFromPath);
    }
  }, [sessionCode, sessionCodeFromPath, setSession]);

  // Redirect if no team selected or no session code
  useEffect(() => {
    // Check localStorage as fallback (store should already have loaded it)
    const storedTeamName = localStorage.getItem('team_name') as TeamName | null;

    // Use store value or fallback to localStorage
    const effectiveTeamName = teamName || storedTeamName;

    // If no session code, redirect to select player to enter code
    if (!effectiveSessionCode) {
      navigate('/select-player', { replace: true });
      return;
    }

    if (!effectiveTeamName) {
      navigate(`/select-player/${effectiveSessionCode}`, { replace: true });
      return;
    }

    if (sessionCodeFromPath !== effectiveSessionCode) {
      navigate(`/game/${effectiveSessionCode}`, { replace: true });
      return;
    }

    // Redirect to end if game over
    if (currentRound > maxRounds) {
      navigate(`/end/${effectiveSessionCode}`, { replace: true });
      return;
    }
  }, [teamName, currentRound, maxRounds, effectiveSessionCode, teamId, navigate, sessionCodeFromPath]);

  // Show first round modal
  useEffect(() => {
    if (currentRound === 1) {
      setShowFirstRoundModal(true);
    }
    // Load last verbatim from localStorage
    const storedVerbatim = localStorage.getItem('last_verbatim');
    if (storedVerbatim && currentRound > 1) {
      setLastVerbatim(storedVerbatim);
    }
  }, [currentRound]);

  // Load game config and criterias
  useEffect(() => {
    const loadGameData = async () => {
      try {
        setLoading(true);

        const sessionCheck = effectiveSessionCode
          ? await sessionsApi.checkSession(effectiveSessionCode)
          : null;
        const sessionLanguage = sessionCheck?.session?.language || i18n.language;
        const sessionMode = sessionCheck?.session?.gameMode ?? null;

        if (sessionCheck?.session) {
          await applyPlayerLanguage(sessionLanguage, sessionMode);
        }

        // Load settings
        const [config, criteriasData] = await Promise.all([
          settingsApi.getGameConfig(),
          criteriasApi.getCriterias(sessionLanguage, effectiveSessionCode ?? undefined),
        ]);
        setGameConfig(config);
        setCriterias(criteriasData);
        setSessionGameMode(sessionMode);
      } catch (error) {
        console.error('Failed to load game data:', error);
      } finally {
        setLoading(false);
      }
    };

    void loadGameData();
  }, [effectiveSessionCode, i18n.language, setGameConfig]);

  useEffect(() => {
    if (!teamName || !effectiveSessionCode) {
      return;
    }

    const socket = getPlayerRealtimeSocket();

    const handleTeamReset = (event: PlayerTeamResetEvent) => {
      if (event.teamName !== teamName || event.sessionCode !== effectiveSessionCode) {
        return;
      }

      clearSelection();
      resetGame();
      navigate(`/select-player/${effectiveSessionCode}`, { replace: true });
    };

    const joinRoom = () => {
      socket.emit(playerRealtimeEvents.joinTeamSession, { sessionCode: effectiveSessionCode, teamName });
    };

    if (!socket.connected) {
      socket.connect();
    }

    joinRoom();
    // Re-join the room after every (re)connection: a reconnected socket has a
    // new id and lost all its room memberships on the server.
    socket.on('connect', joinRoom);
    socket.on(playerRealtimeEvents.teamReset, handleTeamReset);

    return () => {
      socket.off('connect', joinRoom);
      socket.emit(playerRealtimeEvents.leaveTeamSession, { sessionCode: effectiveSessionCode, teamName });
      socket.off(playerRealtimeEvents.teamReset, handleTeamReset);
    };
  }, [clearSelection, effectiveSessionCode, navigate, resetGame, teamName]);

  // Handle add point to criteria
  const handleAddPoint = (criteriaId: number) => {
    if (totalPoints < pointsPerRound) {
      const currentPoints = selectedCriterias[criteriaId] || 0;
      selectCriteria(criteriaId, currentPoints + 1);
    }
  };

  // Handle remove point from criteria
  const handleRemovePoint = (criteriaId: number) => {
    const currentPoints = selectedCriterias[criteriaId] || 0;
    if (currentPoints > 0) {
      selectCriteria(criteriaId, currentPoints - 1);
    }
  };

  // Handle confirm choices (save round and show verbatim)
  const handleConfirm = async () => {
    if (remainingPoints !== 0) {
      return; // Only confirm when all points allocated
    }

    try {
      setSaving(true);
      setSaveError(null);

      // Prepare decisions
      const decisions = Object.entries(selectedCriterias)
        .filter(([_, points]) => points > 0)
        .map(([criteriaId, points]) => ({
          criteriaId: parseInt(criteriaId, 10),
          points,
        }));

      // Persist the whole round as a single logical submission.
      await gameApi.saveRoundBatch({
        decisions,
        roundNumber: currentRound,
        teamName: teamName!,
        sessionCode: effectiveSessionCode ?? undefined,
      });

      // Calculate scores
      const scores = await gameApi.calculateScore({
        teamName: teamName!,
        currentRound,
        sessionCode: effectiveSessionCode ?? undefined,
      });

      // Generate verbatims
      const verbatims = await gameApi.generateVerbatimsHtml({
        type_1: scores.display_1,
        type_2: scores.display_2,
        type_3: scores.display_3,
        type_4: scores.display_4 ?? null,
        lng: i18n.language,
        session: effectiveSessionCode ?? undefined,
      });

      // Store verbatim for next round
      localStorage.setItem('last_verbatim', verbatims);

      // Show verbatim modal
      setVerbatimHtml(verbatims);
      setShowVerbatimModal(true);
    } catch (error) {
      console.error('Failed to save round:', error);
      setSaveError(t('game.save_error', 'Erreur lors de la sauvegarde. Veuillez réessayer.'));
    } finally {
      setSaving(false);
    }
  };

  // Handle next round
  const handleNextRound = () => {
    setShowVerbatimModal(false);
    clearSelection();

    // Last period: go to end screen instead of allowing replay
    if (currentRound >= maxRounds) {
      localStorage.setItem('current_round', String(maxRounds + 1));
      navigate(effectiveSessionCode ? `/end/${effectiveSessionCode}` : '/end', { replace: true });
      return;
    }

    // No full page reload: currentRound change re-renders the board and the
    // [currentRound] effect picks up the verbatim stored by handleConfirm.
    nextRound();
    window.scrollTo({ top: 0 });
  };

  // Parse last verbatim into points for display (3 for teamlab, 4 for leadlab)
  const verbatimPoints = useMemo(() => {
    if (!lastVerbatim) return [];

    // Strip HTML and split by <br /> tags
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = lastVerbatim.replace(/<br\s*\/?>/gi, '|||');
    const text = tempDiv.textContent || tempDiv.innerText || '';

    // Split by separator
    const sentences = text
      .split('|||')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);

    // Teamlab has 3 verbatims; leadlab has 4 (performance, social, sociétal, environnement)
    return sentences.slice(0, isLeadlab ? 4 : 3);
  }, [lastVerbatim, isLeadlab]);

  if (loading) {
    return <Loading fullScreen message={t('loading', 'Chargement...')} />;
  }

  return (
    <div className="h-full flex flex-col relative bg-slate-50">
      {/* Background Layer */}
      <div className="fixed inset-0 -z-50 h-full w-full bg-slate-50 overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#8080800a_1px,transparent_1px),linear-gradient(to_bottom,#8080800a_1px,transparent_1px)] bg-[size:24px_24px]"></div>
        <div className={`absolute left-1/2 top-0 -translate-x-1/2 -z-10 h-[500px] w-[500px] rounded-full ${teamColors.blur} opacity-20 blur-[100px] pointer-events-none`}></div>
        <div className={`absolute right-0 bottom-0 z-0 h-[400px] w-[400px] rounded-full ${
          isLeadlab ? 'bg-teal-500' : 'bg-violet-500'
        } opacity-10 blur-[120px] pointer-events-none`}></div>
      </div>

      {/* Top Navigation Bar */}
      <nav className={`w-full bg-white/60 backdrop-blur-xl border-b ${teamColors.primaryBorderLight}/50 sticky top-0 z-50`}>
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Brand / Round Info */}
            <div className="flex items-center gap-6">
              {/* Team Indicator */}
              {teamName && (
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur px-5 py-2.5 rounded-2xl border border-white/50 shadow-sm cursor-default">
                  <svg className={`w-5 h-5 ${teamColors.primaryText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span className={`text-[10px] font-bold uppercase tracking-widest ${teamColors.primaryText}`}>
                    {t('selectPlayer.teamCaption')}
                  </span>
                  <div className={`w-px h-4 ${teamColors.primaryBorder}`}></div>
                  <span className={`font-black text-xl tabular-nums leading-none ${teamColors.primaryText}`}>
                    {teamName.replace('team', '')}
                  </span>
                </div>
              )}

              <div className="h-8 w-px bg-slate-200/60 hidden sm:block"></div>


              {/* Round Indicator */}
              <div className="flex items-center gap-2.5 bg-white/80 backdrop-blur px-4 py-2 rounded-2xl border border-white/50 shadow-sm transition-all hover:shadow-md cursor-default">
                <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
                <span className="text-sm font-medium text-slate-600">
                  {t('game.period', 'Période')} {currentRound} {t('game.out_of', 'sur')} {maxRounds}
                </span>
              </div>
            </div>

            {/* Right Actions */}
            <div className="flex items-center gap-4">
              {/* Points Counter */}
              <div className={`flex items-center gap-3 ${teamColors.primary} text-white px-5 py-2.5 rounded-2xl shadow-lg ${teamColors.shadow} transition-transform transform hover:scale-105`}>
                <span className="text-[10px] font-bold uppercase tracking-widest opacity-80">
                  {i18n.language === 'fr'
                    ? (remainingPoints <= 1 ? 'Point Restant' : 'Points Restants')
                    : (remainingPoints === 1 ? 'Point Left' : 'Points Left')
                  }
                </span>
                <div className="w-px h-4 bg-white/20"></div>
                <span className="points_left font-black text-xl tabular-nums leading-none">{remainingPoints}</span>
              </div>

              {/* Logout Button */}
              <button
                type="button"
                onClick={async () => {
                  try {
                    // Delete all rounds for this team/session (public endpoint scoped by session code)
                    if (teamName && effectiveSessionCode) {
                      await gameApi.resetTeamAsPlayer(teamName, effectiveSessionCode);
                    }

                    clearPlayerSessionStorage();

                    // Reset store
                    clearSelection();
                    resetGame();

                    // Redirect to select player
                    navigate(
                      effectiveSessionCode ? `/select-player/${effectiveSessionCode}` : '/select-player',
                      { replace: true }
                    );
                  } catch (error) {
                    console.error('Failed to logout:', error);
                    clearPlayerSessionStorage();
                    clearSelection();
                    resetGame();
                    navigate(
                      effectiveSessionCode ? `/select-player/${effectiveSessionCode}` : '/select-player',
                      { replace: true }
                    );
                  }
                }}
                className="group flex items-center justify-center w-10 h-10 rounded-xl bg-white/50 hover:bg-red-50 text-slate-400 hover:text-red-500 border border-slate-200 hover:border-red-200 transition-all shadow-sm"
                title={t('logout', 'Se déconnecter')}
              >
                <svg className="w-5 h-5 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="absolute bottom-0 left-0 h-[3px] bg-slate-100 w-full">
          <div
            className={`h-full bg-gradient-to-r ${teamColors.progressFrom} ${teamColors.progressTo} transition-all duration-300 relative`}
            style={{ width: `${progressPercent}%` }}
          >
            {progressPercent > 2 && (
              <div className={`absolute right-0 top-3 translate-x-1/2 ${teamColors.primary} text-white text-[10px] font-bold px-2 py-0.5 rounded-full shadow-sm`}>
                {Math.round(progressPercent)}%
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Main Dashboard */}
      <main className="flex-grow max-w-[95%] mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 animate__animated animate__fadeIn">
        {/* Header / Context Area (Round 1 only — the previous-period feedback is shown below the levers) */}
        {verbatimPoints.length === 0 && (
          <div className="mb-8">
            {/* Default Title (Round 1) */}
            <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 animate__animated animate__slideInDown">
              <div>
                <h1 className="text-2xl font-bold text-slate-900">
                  {t('game.dashboard_title', 'Gérez vos décisions stratégiques pour cette période')}
                </h1>
                <p className="text-slate-500 mt-1">
                  {t('game.dashboard_subtitle', 'Gérez vos décisions stratégiques pour cette période.')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Criterias Grid — row 1 warm, row 2 cold */}
        <div className="space-y-6">
          {[
            {
              tone: 'warm' as const,
              rowCriterias: criterias.slice(0, 6),
              wrapperClass:
                'rounded-2xl border border-orange-200/80 bg-orange-50/40 p-4',
            },
            {
              tone: 'cold' as const,
              rowCriterias: criterias.slice(6),
              wrapperClass:
                'rounded-2xl border border-sky-200/80 bg-sky-50/40 p-4',
            },
          ].map(({ tone, rowCriterias, wrapperClass }, rowIndex) => (
            <section key={tone} className={wrapperClass}>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {rowCriterias.map((criteria, colorIndex) => {
                  const index = rowIndex * 6 + colorIndex;
                  return (
                    <CriteriaCard
                      key={criteria.id}
                      id={criteria.id}
                      code={criteria.code}
                      name={criteria.name}
                      description={criteria.description}
                      points={selectedCriterias[criteria.id] || 0}
                      index={index}
                      tone={tone}
                      colorIndex={colorIndex}
                      onAdd={() => handleAddPoint(criteria.id)}
                      onRemove={() => handleRemovePoint(criteria.id)}
                      isMaxReached={remainingPoints === 0}
                      canAdd={totalPoints < pointsPerRound}
                    />
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Last Verbatim Section (if exists) — displayed below the levers to keep focus on decisions */}
        {verbatimPoints.length > 0 && (
          <div className="animate__animated animate__fadeInUp w-full mt-8">
            <h3 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <svg className={`w-5 h-5 ${teamColors.primaryText}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
              <span>{t('game.last_verbatim_title', 'Feedback Période Précédente')}</span>
            </h3>

            <div className={`grid grid-cols-1 gap-4 ${
              verbatimPoints.length === 4 ? 'md:grid-cols-2 lg:grid-cols-4' : 'md:grid-cols-3'
            }`}>
              {verbatimPoints.map((point, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm flex gap-4 items-start hover:shadow-md transition-shadow"
                >
                  <div className={`flex-shrink-0 pt-1 ${
                    teamColors.blur === 'bg-indigo-500' ? 'text-indigo-300' :
                    teamColors.blur === 'bg-emerald-500' ? 'text-emerald-300' :
                    teamColors.blur === 'bg-blue-500' ? 'text-blue-300' :
                    'text-orange-300'
                  }`}>
                    <svg className="w-8 h-8 fill-current" viewBox="0 0 24 24" fill="none">
                      <path d="M10 11H6V7H10V11Z" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M6 11V15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M18 11H14V7H18V11Z" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                      <path d="M14 11V15" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                  <p className="text-slate-800 leading-relaxed font-medium text-base pt-1">{point}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Action Bar (Fixed Bottom) */}
      <div
        className={`fixed bottom-8 left-1/2 -translate-x-1/2 z-50 w-80 bg-white border border-slate-200 shadow-2xl shadow-slate-300/50 rounded-2xl p-5 flex flex-col items-center gap-4 transition-all duration-500 transform ${
          remainingPoints === 0 ? 'translate-y-0 opacity-100' : 'translate-y-[200%] opacity-0'
        }`}
      >
        <div className="flex flex-col items-center gap-1 text-center">
          <div className={`w-10 h-1 ${teamColors.primaryLight} rounded-full mb-2`}></div>
          <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
            {t('game.selection_complete', 'Sélection Terminée')}
          </span>
          <h3 className="text-slate-900 font-bold text-lg leading-tight">
            {t('game.ready_to_validate', 'Prêt à valider ?')}
          </h3>
        </div>

        {saveError && (
          <div className="w-full bg-red-50 border border-red-200 rounded-xl p-3">
            <p className="text-sm text-red-700 font-medium text-center">{saveError}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleConfirm}
          disabled={saving || remainingPoints !== 0}
          className={`w-full ${teamColors.primary} text-white text-base font-bold py-3.5 px-6 rounded-xl shadow-lg ${teamColors.shadow} transition-all transform hover:-translate-y-0.5 active:scale-95 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
            teamColors.primary === 'bg-indigo-600' ? 'hover:bg-indigo-700' :
            teamColors.primary === 'bg-emerald-600' ? 'hover:bg-emerald-700' :
            teamColors.primary === 'bg-blue-600' ? 'hover:bg-blue-700' :
            'hover:bg-orange-700'
          }`}
        >
          <span>{saving ? t('game.saving', 'Enregistrement...') : t('game.confirm_choices', 'CONFIRMER')}</span>
          {!saving && (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          )}
        </button>
      </div>

      {/* Modals */}
      <FirstRoundModal
        isOpen={showFirstRoundModal}
        onClose={() => setShowFirstRoundModal(false)}
        teamName={teamName}
        gameMode={gameMode}
      />
      <VerbatimModal
        isOpen={showVerbatimModal}
        verbatimHtml={verbatimHtml}
        currentRound={currentRound}
        teamId={teamId}
        sessionCode={effectiveSessionCode}
        teamName={teamName}
        gameMode={gameMode}
        onNext={handleNextRound}
      />

      {/* Debug Footer */}
      <footer className="fixed bottom-0 right-0 bg-slate-50/80 backdrop-blur-sm border-t border-l border-slate-200 py-1.5 px-4 z-30 rounded-tl-lg">
        <div className="flex items-center gap-4 text-xs text-slate-600">
          <span>
            <span className="font-semibold">Mode:</span>{' '}
            <span className="ml-1 capitalize">{gameMode || <span className="text-red-500 italic">non défini</span>}</span>
          </span>
          <span>
            <span className="font-semibold">Session:</span>{' '}
            <span className="ml-1 font-mono font-bold">{effectiveSessionCode || <span className="text-red-500 italic">non définie</span>}</span>
          </span>
        </div>
      </footer>
    </div>
  );
};
